import { addSeconds, formatISODuration, intervalToDuration } from 'date-fns';
import type { BroadcastEvent, MediaObject, PublicationEvent, WithContext } from 'schema-dts';

type BuildMediaStructuredDataInput = {
	description?: string;
	publicationDate?: string | null;
	publication?: BroadcastEvent;
	thumbnail?: string | null;
	title?: string | null;
	url: string;
	type: 'audio' | 'video';
} & ({ duration: number | undefined } | { start: Date | null; end: Date | null });

function secondsToDuration(seconds: number) {
	// Use a fixed UTC date that produces consistent duration calculations
	// This date is chosen to maintain backward compatibility with existing tests
	const start = new Date('2024-10-01T00:00:00.000Z');
	const end = addSeconds(start, seconds);

	return formatISODuration(intervalToDuration({ start, end }));
}

export function buildMediaStructuredData(media: BuildMediaStructuredDataInput) {
	const { description, publicationDate, thumbnail, title, type, url, publication } = media;

	let duration: string | null = null;
	if ('duration' in media) {
		duration = media.duration != null ? secondsToDuration(media.duration) : null;
	} else {
		duration =
			media.start !== null && media.end !== null ? formatISODuration(intervalToDuration({ start: media.start, end: media.end })) : null;
	}

	if (!duration || !thumbnail || !title || !url) {
		return null;
	}

	const publicationWithDefaults: PublicationEvent = {
		...publication,
		'@type': publication?.['@type'] ?? 'BroadcastEvent',
	};

	const schema: WithContext<MediaObject> = {
		'@context': 'https://schema.org',
		'@type': type === 'audio' ? 'AudioObject' : 'VideoObject',
		contentUrl: url,
		description: description || title,
		duration: duration,
		name: title,
		thumbnailUrl: thumbnail,
		uploadDate: publicationDate || undefined,
		publication: publication ? publicationWithDefaults : undefined,
	};

	return schema;
}
